// The images a reseller has uploaded on /bestsellers → Upload bestsellers.
//
// Several images, unlike Browse's image search, which holds exactly one. That is the point of
// the tab: a reseller's real bestseller list is several pictures, often several DIFFERENT
// STYLES, and often work that is not on Pictufy at all. See `search/imageClusters.ts` for
// why they are clustered rather than averaged — averaging is the documented failure mode.
//
// Each file is embedded ONCE, when it is picked (`search.vector` with `returnVec`), and only
// the 1152-float vector is kept. The file itself never leaves the browser.
import { api } from "../../api";
import { toastError } from "../../ui/messages";
import { downscaleForEmbedding, vecToB64 } from "../searchImage/downscale";

export interface Upload {
  /** Stable id for the remove button and the preview key. */
  id: string;
  name: string;
  /** Downscaled `data:` URL — the thumbnail. */
  preview: string;
  /** Base64 of the raw `Float32Array` bytes, as `search.vector` returns them. */
  vecB64: string;
}

/**
 * The cap, matching `MAX_UPLOADS` on the server.
 *
 * Duplicated rather than imported: the server module is not reachable from the bundle, and
 * the number is in the error message either way. The server is the one that enforces it.
 */
export const MAX_UPLOADS = 12;

let uploads: Upload[] = [];
type Listener = (list: Upload[]) => void;
const listeners = new Set<Listener>();

export function getUploads(): Upload[] {
  return uploads;
}

export function subscribeUploads(fn: Listener): () => void {
  listeners.add(fn);
  fn(uploads);
  return () => listeners.delete(fn);
}

function emit(): void {
  for (const fn of listeners) fn(uploads);
}

export function removeUpload(id: string): void {
  uploads = uploads.filter((u) => u.id !== id);
  emit();
}

export function clearUploads(): void {
  uploads = [];
  emit();
}

/**
 * Embed and add files. Returns how many were added.
 *
 * Added one at a time as each embed resolves, so thumbnails appear progressively and a
 * failure half way through keeps the ones that worked — the same shape as task attachments,
 * and for the same reason: a set of five photographs is the normal case here.
 */
export async function addUploads(files: File[]): Promise<number> {
  const images = files.filter((f) => f.type.startsWith("image/"));
  if (images.length < files.length) toastError("Only images can be uploaded here");
  if (images.length === 0) return 0;

  const room = MAX_UPLOADS - uploads.length;
  if (room <= 0) {
    toastError(`You can compare up to ${String(MAX_UPLOADS)} images at a time`);
    return 0;
  }
  const take = images.slice(0, room);
  if (take.length < images.length) {
    toastError(`Only the first ${String(room)} were added — the limit is ${String(MAX_UPLOADS)}`);
  }

  let added = 0;
  for (const file of take) {
    try {
      const { b64, preview } = await downscaleForEmbedding(file);
      // `k: 1` because the ranked results are thrown away — only `returnVec` is wanted, which
      // is what that flag exists for. Same call Browse's image search makes.
      const res = await api.search.vector({ imageB64: b64, k: 1, returnVec: true });
      if (!res.queryVec) throw new Error("The image could not be read");
      uploads = [
        ...uploads,
        {
          id: `${String(Date.now())}-${String(added)}-${file.name}`,
          name: file.name,
          preview,
          vecB64: vecToB64(res.queryVec),
        },
      ];
      added += 1;
      emit();
    } catch (err: unknown) {
      toastError(err instanceof Error ? err.message : `Could not read ${file.name}`);
    }
  }
  return added;
}
